/**
 * Settings Screen - Delete All Data Actions
 *
 * DD-372 [M4.4]: GDPR Art. 17 - Right to Erasure
 * Self-service "Delete My Data" with cascade deletion:
 * 1. Cloud: Edge Function (DB + Redis + Storage) — now via the validated
 *    `useDeleteUserData` hook (DTO migration Wave 4D, NocoDB #3743)
 * 2. Local: Zustand store + React Query cache + AsyncStorage
 *
 * Requires double confirmation for destructive action.
 *
 * DTO migration note: The direct-fetch call to `delete-user-data` that used
 * to live inline here was eliminated in Wave 4D. Cloud deletion now goes
 * through the shared `deleteUserData()` helper, which parses the server
 * response through `DeleteUserDataResultDto` — schema drift on the backend
 * surfaces as an immediate error instead of silently broken UI.
 */

import { QueryClient } from '@tanstack/react-query';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { TFunc } from './types';
import { queryKeys } from '@dramadetector/api-client';
import { captureEvent } from '../analytics';
import { deleteUserData } from '../../hooks/useDeleteUserData';

export interface DeleteDataActionsParams {
  user: { id: string; createdAt?: string } | null;
  alert: (title: string, message?: string, buttons?: any[], options?: any) => void;
  t: TFunc;
  tCommon: TFunc;
  queryClient: QueryClient;
  clearLocalHistory: () => void;
}

/**
 * Handles full data deletion with double confirmation.
 *
 * Workflow:
 * 1. Show destructive confirmation with explanation
 * 2. On confirm: Call delete-user-data via `deleteUserData()` (validated DTO)
 * 3. Clear all local stores (Zustand + React Query + AsyncStorage)
 * 4. Track analytics event
 * 5. Show success/error alert
 */
export function handleDeleteAllData(params: DeleteDataActionsParams): void {
  const { user, alert, t, tCommon, queryClient, clearLocalHistory } = params;

  if (!user) {
    alert(tCommon('error'), t('delete_data.requires_auth'));
    return;
  }

  alert(
    t('delete_data.confirm_title'),
    t('delete_data.confirm_message'),
    [
      { text: tCommon('cancel'), style: 'cancel' },
      {
        text: t('delete_data.confirm_button'),
        style: 'destructive',
        onPress: async () => {
          try {
            // 1. Cloud deletion via validated DTO helper (DB + Redis + crypto-shred)
            const result = await deleteUserData();

            captureEvent('feature_used', {
              feature: 'delete_all_data',
              screen: 'settings',
              gdpr_article: '17',
              user_tenure_days: user.createdAt
                ? Math.floor((Date.now() - new Date(user.createdAt).getTime()) / 86400000)
                : 0,
            });

            // 2. Clear local Zustand store
            clearLocalHistory();

            // 3. Clear all React Query caches
            queryClient.removeQueries({ queryKey: queryKeys.analysis.all(user.id) });
            queryClient.clear();

            // 4. Clear AsyncStorage (all app caches)
            try {
              const allKeys = await AsyncStorage.getAllKeys();
              const dramaKeys = allKeys.filter(k => k.startsWith('@DramaDetector/') || k.startsWith('DRAMADETECTOR_'));
              if (dramaKeys.length > 0) {
                await AsyncStorage.multiRemove(dramaKeys);
              }
            } catch (storageError) {
              console.warn('[Delete Data] AsyncStorage clear failed:', storageError);
            }

            // 5. Show success — the DTO's `database` record is keyed by table
            // name; the RPC inserts a `total` roll-up key we prefer for the
            // toast, falling back to 0 when the RPC ever drops it.
            const totalDeleted = result.deleted.database.total ?? 0;
            alert(
              t('delete_data.success_title'),
              t('delete_data.success_message', { count: totalDeleted })
            );
          } catch (error) {
            console.error('[Delete Data] Unexpected error:', error);
            alert(tCommon('error'), t('delete_data.error'));
          }
        },
      },
    ]
  );
}
